import React, { useState, useEffect, useRef } from "react";
import { User, LogIn, UserPlus, HelpCircle, ArrowLeft, Info, Phone, Search, Lock, Mail, Loader2, Eye, EyeOff, CheckSquare, Square, AlertCircle, CheckCircle2, Check } from "lucide-react";
import { useModal } from "../contexts/ModalContext";
import AppInput from "./ui/AppInput";
import AppButton from "./ui/AppButton";
import {
  DEFAULT_THEME,
  DEFAULT_THEME_KEY,
  THEME_PRESET_OPTIONS,
  normalizeThemeKey,
  resolveThemeColorClass,
  isThemeContrastAccessible
} from "../constants/theme";

import ShowcaseBackground from "./ShowcaseBackground"; // [NEW]

const PRIMARY_BG_CLASS = DEFAULT_THEME.bg;
const PRIMARY_TEXT_CLASS = DEFAULT_THEME.text;
const PRIMARY_BORDER_CLASS = DEFAULT_THEME.border;
const PRIMARY_HOVER_TEXT_CLASS = "hover:text-[#354F38]";
const PRIMARY_GROUP_FOCUS_TEXT_CLASS = "group-focus-within:text-[#354F38]";
const PRIMARY_CARET_CLASS = "caret-[#354F38]";
const PRIMARY_DEFAULT_GRADIENT_CLASS = "from-[#354F38] to-[#253829] hover:from-[#2C3E30] hover:to-[#1B261C] shadow-[0_10px_20px_-10px_rgba(53,79,56,0.4)]";

export default function Login({ handleGoogleLogin, handleGuestLogin, handleEmailLogin, handleSignup, handleResetPassword, handleFindId, handleKakaoLogin, handleNaverLogin, pendingSocialData, onCancelSocialSignup }) {
  const [mode, setMode] = useState("login");
  const [findType, setFindType] = useState("id");
  const isNaverLoginAvailable = false;
  const [inAppBrowser, setInAppBrowser] = useState({ isInApp: false, isKakaoTalk: false });

  // [NEW] Effect to switch to social_signup mode if pending data exists
  useEffect(() => {
    if (pendingSocialData) {
      setMode("social_signup");
      setId(pendingSocialData.email || "");
      setName(pendingSocialData.name || "");
      // Reset other fields
      setPhone(""); setRole(""); setChurchName(""); setDeptName(""); setDeptThemeKey(DEFAULT_THEME_KEY); setAgreed(false);
    } else {
      // [FIX] 가입 취소 시 로그인 모드로 복귀
      setMode("login");
    }
  }, [pendingSocialData]);

  // [NEW] In-app browser detection (KakaoTalk/Instagram/Facebook/Line etc.)
  useEffect(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const isKakaoTalk = ua.includes("kakaotalk");
    const isInApp =
      isKakaoTalk ||
      ua.includes("fbav") ||
      ua.includes("fban") ||
      ua.includes("instagram") ||
      ua.includes("line") ||
      ua.includes("wv");

    setInAppBrowser({ isInApp, isKakaoTalk });
  }, []);

  // const [signupType, setSignupType] = useState("simple"); // Removed Simple ID
  const { openAlert } = useModal();

  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [churchName, setChurchName] = useState("");
  const [deptName, setDeptName] = useState("");
  // [NEW] Department Color State
  const [deptThemeKey, setDeptThemeKey] = useState(DEFAULT_THEME_KEY);

  // [New] Mouse Spotlight State
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // [New] UX States
  const [isLoading, setIsLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false); // [New] Privacy Consent

  // [New] Modern UX States
  const [errors, setErrors] = useState({}); // { id: "이메일 형식이 아닙니다", pw: "너무 짧습니다" }
  const [isShaking, setIsShaking] = useState(false);

  // [New] Real-time Validation Logic
  const validateField = (field, value) => {
    let newError = "";
    if (field === "id") {
      if (!value) newError = "이메일을 입력해주세요.";
      else if (!value.includes("@") || !value.includes(".")) newError = "올바른 이메일 형식이 아닙니다.";
    }
    if (field === "pw") {
      if (!value) newError = "비밀번호를 입력해주세요.";
      else if (value.length < 6) newError = "비밀번호는 6자 이상이어야 합니다.";
    }
    if (field === "name" && !value) newError = "이름을 입력해주세요.";
    if (field === "phone" && !value) newError = "연락처를 입력해주세요.";
    if (field === "churchName" && !value) newError = "교회명을 입력해주세요.";
    if (field === "deptName" && !value) newError = "부서명을 입력해주세요.";

    return newError;
  };

  const handleBlur = (field, value) => {
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (field, value, setter) => {
    setter(value);
    // Real-time clear error if valid
    if (errors[field]) {
      const error = validateField(field, value);
      if (!error) setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };


  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const resetFields = () => { setId(""); setPw(""); setName(""); setPhone(""); setRole(""); setChurchName(""); setDeptName(""); setDeptThemeKey(DEFAULT_THEME_KEY); };

  const handleOpenExternalBrowser = async () => {
    const currentUrl = window.location.href;

    if (inAppBrowser.isKakaoTalk) {
      window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`;
      return;
    }

    try {
      await navigator.clipboard.writeText(currentUrl);
      openAlert("현재 링크를 복사했습니다.\n사파리/크롬에서 붙여넣어 접속해주세요.", "외부 브라우저 권장");
    } catch (e) {
      openAlert("인앱브라우저에서는 로그인 오류가 발생할 수 있습니다.\n외부 브라우저(사파리/크롬)로 열어주세요.", "외부 브라우저 권장");
    }
  };

  const runSocialLogin = (handler, providerName) => {
    if (inAppBrowser.isInApp) {
      openAlert(
        `인앱브라우저에서는 ${providerName} 로그인 오류가 자주 발생합니다.\n외부 브라우저(사파리/크롬)에서 다시 시도해주세요.`,
        "외부 브라우저 권장"
      );
      return;
    }
    handler();
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return; // Prevent double submit

    // [New] Validate All
    const newErrors = {};
    const fieldsToCheck = (mode === "signup" || mode === "social_signup")
      ? ["name", "phone", "churchName", "deptName"]
      : (mode === "find" ? (findType === "id" ? ["name", "phone"] : ["id"]) : ["id", "pw"]);

    // Add Email/PW check only for regular signup/login
    if (mode === "signup" || mode === "login" || mode === "email_login") {
      fieldsToCheck.push("id");
    }
    if (mode === "signup" || mode === "login" || mode === "email_login") {
      // PW check only if manual input
      if (mode !== "login" && mode !== "email_login") fieldsToCheck.push("pw");
    }

    // Map values for validation
    const values = { id, pw, name, phone, churchName, deptName };

    fieldsToCheck.forEach(field => {
      const error = validateField(field, values[field]);
      if (error) newErrors[field] = error;
    });

    if ((mode === "signup" || mode === "social_signup") && !agreed) {
      // [New] Shake for consent too
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      openAlert("개인정보 수집 및 이용에 동의해주세요.", "알림"); // Keep alert for checkbox for now or maybe highlight it
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return; // STOP Here
    }

    setIsLoading(true);
    try {
      // ... (Logic kept same)
      if (mode === "find") {
        if (findType === "id") {
          if (!name || !phone) {
            openAlert("이름과 연락처를 입력해주세요.", "알림");
            return;
          }
          await handleFindId(name, phone);
        } else {
          if (!id || !id.includes("@")) {
            openAlert("가입한 이메일 주소를 입력해주세요.", "알림");
            return;
          }
          await handleResetPassword(id);
        }
        return;
      }

      if (mode === "login" || mode === "email_login") {
        if (!id || !pw) {
          openAlert("아이디/비번을 입력해주세요.", "알림");
          return;
        }
      }

      if (mode === "signup" || mode === "social_signup") {

        if (!name || !phone || !churchName || !deptName) {
          openAlert("정보를 모두 입력해주세요.", "알림");
          return;
        }
        const normalizedDeptThemeKey = normalizeThemeKey(deptThemeKey);
        if (!isThemeContrastAccessible(normalizedDeptThemeKey)) {
          openAlert("선택한 부서 색상이 대비 기준(4.5:1)을 충족하지 못합니다.", "접근성 기준");
          return;
        }
        const resolvedDeptColor = resolveThemeColorClass(normalizedDeptThemeKey);
        const departmentThemeKeys = { [deptName]: normalizedDeptThemeKey };
        const departmentColors = { [deptName]: resolvedDeptColor };

        // Enforce Email Signup & Social
        const emailToUse = id;

        const additionalInfo = {
          name,
          phone,
          role: role || "전도사",
          churchName,
          deptName,
          deptThemeKey: normalizedDeptThemeKey,
          deptColor: resolvedDeptColor,
          departmentThemeKeys,
          departmentColors,
          signupType: mode === "social_signup" ? (pendingSocialData.provider || "social") : "email"
        };

        // Pass to handler. 
        // For social_signup, 'pw' might be ignored or handled by parent.
        await handleSignup(emailToUse, pw, additionalInfo);

      } else {
        // Login Logic
        const emailToUse = id; // Assume ID is Email
        if (!id.includes("@")) {
          openAlert("이메일 주소를 입력해주세요.", "알림");
          return;
        }
        await handleEmailLogin(emailToUse, pw);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Shake Animation Style
  const shakeStyle = isShaking ? { animation: "shake 0.4s cubic-bezier(.36,.07,.19,.97) both" } : {};

  return (
    <div
      ref={containerRef}
      className="flex min-h-screen items-center justify-center p-6 relative overflow-hidden font-sans text-gray-800"
    >
      <style>{`
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
      `}</style>
      {/* 1. Dynamic Mockup Background */}
      <ShowcaseBackground />

      {/* 2. Main Login Card (Clean SaaS Style - Soft Glass) */}
      <div
        style={shakeStyle}
        className="max-w-[420px] w-full bg-white/70 backdrop-blur-2xl rounded-[2.5rem] p-10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] border border-white/60 relative z-10 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] transition-all duration-500"
      >

        {/* Header */}
        <div className="text-center mb-10">

          <img src="/logotext.png" alt="The STAFF" className="h-24 mx-auto mb-4 object-contain drop-shadow-sm" />
          <p className="text-gray-800 text-sm font-black tracking-widest uppercase">
            {mode === "login" && "당신의 사역을 지탱합니다"}
            {mode === "signup" && "당신의 사역을 지탱합니다"}
            {mode === "social_signup" && "Welcome Aboard"}
            {mode === "find" && "Account Recovery"}
          </p>
        </div>

        {inAppBrowser.isInApp && (
          <div className="mb-6 p-4 rounded-2xl border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-2 text-amber-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-xs font-black">인앱브라우저 감지됨</p>
                <p className="text-[11px] mt-1 leading-relaxed">
                  카카오/인스타/페북 앱 내 브라우저에서는 로그인/회원가입 오류가 발생할 수 있습니다.
                </p>
                <button
                  type="button"
                  onClick={handleOpenExternalBrowser}
                  className="mt-2 text-[11px] font-bold px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-200 transition"
                >
                  외부 브라우저로 열기
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mode: Find */}
        {mode === "find" && (
          <form onSubmit={onSubmit} className="animate-fade-in-up space-y-4">
            <div className="flex bg-gray-100/50 p-1 rounded-2xl border border-gray-200/50">
              <button type="button" onClick={() => setFindType("id")} className={`flex-1 py-3 text-xs font-extrabold rounded-xl transition-all ${findType === "id" ? "bg-white text-[#1B3323] shadow-md" : "text-gray-400 hover:text-gray-600"}`}>아이디 찾기</button>
              <button type="button" onClick={() => setFindType("pw")} className={`flex-1 py-3 text-xs font-extrabold rounded-xl transition-all ${findType === "pw" ? "bg-white text-[#1B3323] shadow-md" : "text-gray-400 hover:text-gray-600"}`}>비밀번호 찾기</button>
            </div>
            {findType === "id" ? (
              <div className="space-y-3">
                <ModernInput icon={User} label="이름" value={name} onChange={setName} />
                <ModernInput icon={Phone} label="연락처 (숫자만)" value={phone} onChange={setPhone} />
              </div>
            ) : (
              <div className="space-y-2">
                <ModernInput icon={Mail} label="가입한 이메일" value={id} onChange={setId} />
                <p className="text-[11px] text-gray-400 pl-2 opacity-80">* 가입하신 이메일로 링크가 발송됩니다.</p>
              </div>
            )}
            <ModernButton text={findType === "id" ? "아이디 찾기" : "링크 발송"} />
            {/* Footer Links */}
            <div className="flex justify-center gap-8 text-xs text-gray-400 mt-4 font-bold tracking-wide">
              <button type="button" onClick={() => { setMode("login"); resetFields(); }} className={`${PRIMARY_HOVER_TEXT_CLASS} transition-all`}>로그인으로 돌아가기</button>
            </div>
          </form>
        )}

        {/* Mode: Signup (Social Only) */}
        {mode === "signup" && (
          <div className="animate-fade-in-up space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-xl font-black text-gray-800 tracking-tight">가입 방식 선택</h3>
              <p className="text-xs font-medium text-gray-400 mt-1">간편하게 소셜 계정으로 시작해보세요.</p>
            </div>

            <div className="space-y-3">
              {/* Kakao Button */}
              <button
                type="button"
                onClick={() => runSocialLogin(handleKakaoLogin, "카카오")}
                className="w-full bg-[#FEE500] hover:bg-[#FDD835] text-[#191919] font-medium py-4 rounded-2xl transition-all flex items-center justify-center gap-3 relative shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="absolute left-6 text-[#191919]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3C6.48 3 2 6.48 2 10.77C2 13.63 3.9 16.1 6.78 17.5L5.93 20.6C5.87 20.82 6.13 21 6.33 20.86L10.53 18C11.01 18.06 11.5 18.09 12 18.09C17.52 18.09 22 14.61 22 10.32C22 6.03 17.52 3 12 3Z" />
                  </svg>
                </div>
                <span className="text-[15px] font-bold">카카오로 시작하기</span>
              </button>

              {/* Naver Button */}
              <button
                type="button"
                onClick={() => {
                  if (isNaverLoginAvailable) {
                    runSocialLogin(handleNaverLogin, "네이버");
                  } else {
                    openAlert("네이버 정식 앱 등록 후 사용 가능합니다.", "준비 중");
                  }
                }}
                className={`w-full text-white font-medium py-4 rounded-2xl transition-all flex items-center justify-center gap-3 relative shadow-sm ${isNaverLoginAvailable ? "bg-[#03C75A] hover:bg-[#02b351] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0" : "bg-[#03C75A]/70"}`}
              >
                <div className="absolute left-6">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727v12.845z" />
                  </svg>
                </div>
                <span className="text-[15px] font-bold">네이버로 시작하기</span>
                {!isNaverLoginAvailable && (
                  <span className="absolute right-4 text-[10px] font-black px-2 py-0.5 rounded-full bg-white/20 border border-white/30">
                    준비중
                  </span>
                )}
              </button>

              {/* Google Button */}
              <button
                type="button"
                onClick={() => runSocialLogin(handleGoogleLogin, "구글")}
                className="w-full bg-white border border-gray-200 text-gray-600 font-medium py-4 rounded-2xl hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all flex items-center justify-center gap-3 relative hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="absolute left-6">
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                </div>
                <span className="text-[15px] font-bold">구글로 시작하기</span>
              </button>
            </div>

            {/* Footer Links */}
            <div className="flex justify-center gap-8 text-xs text-gray-400 mt-6 font-bold tracking-wide">
              <button type="button" onClick={() => { setMode("login"); resetFields(); }} className={`${PRIMARY_HOVER_TEXT_CLASS} transition-all`}>로그인으로 돌아가기</button>
            </div>
          </div>
        )}

        {/* Mode: Social Signup Completion [NEW] */}
        {mode === "social_signup" && (
          <form onSubmit={onSubmit} className="animate-fade-in-up space-y-4">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 mb-3 shadow-sm">
                {pendingSocialData?.provider === 'kakao' && <div className="w-2 h-2 rounded-full bg-[#FEE500]"></div>}
                {pendingSocialData?.provider === 'naver' && <div className="w-2 h-2 rounded-full bg-[#03C75A]"></div>}
                {(!pendingSocialData?.provider || pendingSocialData?.provider === 'google') && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                <span className={`${PRIMARY_TEXT_CLASS} text-[11px] font-extrabold tracking-tight`}>
                  {pendingSocialData?.provider === 'kakao' ? '카카오' : pendingSocialData?.provider === 'naver' ? '네이버' : '구글'}
                  계정으로 가입 진행 중
                </span>
              </div>
              <h3 className="text-xl font-black text-gray-800 tracking-tight">마지막 단계입니다!</h3>
              <p className="text-xs font-medium text-gray-400 mt-1">원활한 사역 관리를 위해 필수 정보를 입력해주세요.</p>
            </div>

            <ModernInput
              value={id}
              label="이메일 (자동 입력됨)"
              className="opacity-60 pointer-events-none select-none bg-gray-100"
              onChange={() => { }} // No-op
            />
            {/* Password hidden for social signup */}

            <div className="space-y-3 pt-2">
              <div className="flex gap-3">
                <ModernInput value={name} onChange={setName} label="이름" className="flex-1" placeholder="홍길동" />
                <ModernInput value={phone} onChange={setPhone} label="연락처" className="flex-1" placeholder="010.." />
              </div>
              <ModernInput value={role} onChange={setRole} label="직분" placeholder="전도사, 부장..." />

              {/* Department Row */}
              <div className="flex gap-3">
                <ModernInput value={churchName} onChange={setChurchName} label="교회명" className="flex-1" />
                <ModernInput value={deptName} onChange={setDeptName} label="부서명" className="flex-1" />
              </div>

              {/* Department Color Picker (Same as signup) */}
              <div className="space-y-1.5 pt-1">
                <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">부서 색상 선택</label>
                <div className="flex gap-2 p-2 bg-gray-50 rounded-2xl border border-gray-100 overflow-x-auto custom-scrollbar">
                  {THEME_PRESET_OPTIONS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setDeptThemeKey(c.key)}
                      className={`
                        w-8 h-8 rounded-full flex-shrink-0 transition-all duration-300 shadow-sm border-2
                        ${deptThemeKey === c.key ? 'border-gray-800 scale-110 ring-2 ring-gray-200' : 'border-transparent hover:scale-105'}
                        ${c.swatchClass}
                      `}
                      title={`${c.label} (${c.primaryHex})`}
                    />
                  ))}
                </div>
              </div>

              {/* Privacy Consent (Mandatory for Compliance) */}
              <div className="mt-4 p-4 bg-gray-50/50 rounded-2xl border border-gray-100/50 hover:bg-white transition-colors duration-300">
                <div
                  className="flex items-start gap-3 cursor-pointer group"
                  onClick={() => setAgreed(!agreed)}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-300 ${agreed ? `${PRIMARY_BG_CLASS} ${PRIMARY_BORDER_CLASS}` : "border-gray-300 group-hover:border-gray-400"}`}>
                    {agreed && <Check size={14} className="text-white" strokeWidth={3} />}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-bold text-gray-700 select-none">
                      [필수] 개인정보 수집 및 이용 동의 <span className="text-rose-500">*</span>
                    </p>
                    <p className="text-[10px] text-gray-400 leading-tight font-medium">
                      TheSTAFF 서비스 제공을 위해 <strong>이름, 연락처, 교회명, 부서명, 직분</strong>을 수집·이용합니다.
                      수집된 정보는 서비스 탈퇴 시까지 보관되며, <span className="underline decoration-gray-200 underline-offset-2">관련 법령에 따라 안전하게 보호됩니다.</span> 동의를 거부할 권리가 있으나, 거부 시 서비스 이용이 제한됩니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ModernButton text="가입 완료 및 시작하기" isLoading={isLoading} deptThemeKey={deptThemeKey} />

            <div className="flex justify-center gap-8 text-xs text-gray-400 mt-4 font-bold tracking-wide">
              <button type="button" onClick={onCancelSocialSignup} className="hover:text-rose-500 transition-all flex items-center gap-1 cursor-pointer">
                <ArrowLeft size={12} /> 가입 취소
              </button>
            </div>
          </form>
        )}

        {/* Mode: Email Login (Legacy) */}
        {mode === "email_login" && (
          <form onSubmit={onSubmit} className="animate-fade-in-up space-y-4">
            <div className="flex items-center mb-2">
              <button type="button" onClick={() => setMode("login")} className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all">
                <ArrowLeft size={20} />
              </button>
              <span className="text-sm font-bold text-gray-500 ml-1">이메일 로그인</span>
            </div>

            <ModernInput
              value={id}
              onChange={(v) => handleChange("id", v, setId)}
              onBlur={() => handleBlur("id", id)}
              label="이메일"
              placeholder="name@example.com"
              error={errors.id}
            />
            <ModernInput
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={(v) => handleChange("pw", v, setPw)}
              onBlur={() => handleBlur("pw", pw)}
              label="비밀번호"
              placeholder="••••••••"
              endIcon={showPw ? EyeOff : Eye}
              onEndIconClick={() => setShowPw(!showPw)}
              error={errors.pw}
            />
            <ModernButton text="로그인" isLoading={isLoading} />
            {/* Footer Links */}
            <div className="flex justify-center gap-8 text-xs text-gray-400 mt-4 font-bold tracking-wide">
              <button type="button" onClick={() => { setMode("find"); resetFields(); }} className={`${PRIMARY_HOVER_TEXT_CLASS} transition-all relative group`}>
                정보 찾기
              </button>
            </div>
          </form>
        )}

        {/* Mode: Login (Social First) */}
        {mode === "login" && (
          <div className="animate-fade-in-up space-y-4">
            <div className="space-y-3">
              {/* Kakao Button */}
              <button
                type="button"
                onClick={() => runSocialLogin(handleKakaoLogin, "카카오")}
                className="w-full bg-[#FEE500] hover:bg-[#FDD835] text-[#191919] font-medium py-4 rounded-2xl transition-all flex items-center justify-center gap-3 relative shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="absolute left-6 text-[#191919]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3C6.48 3 2 6.48 2 10.77C2 13.63 3.9 16.1 6.78 17.5L5.93 20.6C5.87 20.82 6.13 21 6.33 20.86L10.53 18C11.01 18.06 11.5 18.09 12 18.09C17.52 18.09 22 14.61 22 10.32C22 6.03 17.52 3 12 3Z" />
                  </svg>
                </div>
                <span className="text-[15px] font-bold">카카오로 계속하기</span>
              </button>

              {/* Naver Button */}
              <button
                type="button"
                onClick={() => {
                  if (isNaverLoginAvailable) {
                    runSocialLogin(handleNaverLogin, "네이버");
                  } else {
                    openAlert("네이버 정식 앱 등록 후 사용 가능합니다.", "준비 중");
                  }
                }}
                className={`w-full text-white font-medium py-4 rounded-2xl transition-all flex items-center justify-center gap-3 relative shadow-sm ${isNaverLoginAvailable ? "bg-[#03C75A] hover:bg-[#02b351] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0" : "bg-[#03C75A]/70"}`}
              >
                <div className="absolute left-6">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727v12.845z" />
                  </svg>
                </div>
                <span className="text-[15px] font-bold">네이버로 계속하기</span>
                {!isNaverLoginAvailable && (
                  <span className="absolute right-4 text-[10px] font-black px-2 py-0.5 rounded-full bg-white/20 border border-white/30">
                    준비중
                  </span>
                )}
              </button>

              {/* Google Button */}
              <button
                type="button"
                onClick={() => runSocialLogin(handleGoogleLogin, "구글")}
                className="w-full bg-white border border-gray-200 text-gray-600 font-medium py-4 rounded-2xl hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all flex items-center justify-center gap-3 relative hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="absolute left-6">
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                </div>
                <span className="text-[15px] font-bold">Google로 계속하기</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white/0 backdrop-blur-sm text-gray-400 font-medium">또는</span>
              </div>
            </div>

            {/* Guest / Email Toggle */}
            <div className="flex gap-2 justify-center">
              <button type="button" onClick={handleGuestLogin} className="w-full bg-gray-100 text-gray-500 font-bold py-3.5 rounded-2xl hover:bg-gray-200 transition-all flex items-center justify-center gap-2 text-sm">
                <User size={16} /> 게스트로 체험하기
              </button>
            </div>

            {/* Signup Link */}
            <div className="mt-6 text-center">
              <span className="text-xs text-gray-400">계정이 없으신가요? </span>
              <button onClick={() => setMode("signup")} className={`text-xs font-bold ${PRIMARY_TEXT_CLASS} hover:underline`}>회원가입</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// [Sub Component] Modern Input (Refined)
const ModernInput = ({ icon: Icon, label, value, onChange, onBlur, type = "text", placeholder, className = "", endIcon: EndIcon, onEndIconClick, error }) => (
  <div className={`space-y-1.5 ${className}`}>
    <div className="flex justify-between items-center px-1">
      <label className={`text-[10px] font-extrabold transition-colors uppercase tracking-wider ${error ? "text-rose-500" : "text-gray-400"}`}>{label}</label>
      {error && (
        <p className="text-[10px] text-rose-500 font-bold animate-fade-in-right flex items-center gap-1">
          <AlertCircle size={10} /> {error}
        </p>
      )}
    </div>
    <div className="relative group">
      {Icon && <Icon size={16} className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${error ? "text-rose-400" : `text-gray-300 ${PRIMARY_GROUP_FOCUS_TEXT_CLASS}`}`} />}
      <AppInput
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`
                    block w-full px-4 py-3.5 ${Icon ? 'pl-11' : 'pl-4'} ${EndIcon ? 'pr-11' : 'pr-4'}
                    text-sm font-bold text-gray-900 placeholder-gray-400 ${PRIMARY_CARET_CLASS}
                    rounded-2xl
                    transition-all duration-300
                    ${error
            ? "border-rose-200 focus:border-rose-500 bg-rose-50"
            : "focus:ring-1"
          }
                    focus:ring-0
                `}
        placeholder={placeholder}
      />
      {EndIcon && (
        <button
          type="button"
          onClick={onEndIconClick}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 transition-colors focus:outline-none hover:scale-110 transform duration-200"
        >
          <EndIcon size={18} />
        </button>
      )}

      {/* Validation Icon (Keep inside for visual cue) */}
      {error && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-rose-500 animate-fade-in-right bg-white rounded-full p-1 shadow-sm">
          {!EndIcon && <AlertCircle size={16} />}
        </div>
      )}
    </div>
  </div>
);

// [Sub Component] Modern Button (Gradient)
const ModernButton = ({ text, onClick, className = "", isLoading = false, deptThemeKey }) => {
  // Map of Dept Colors to Gradients (Manual Mapping for better aesthetics)
  const getGradientClass = (themeKey) => {
    switch (normalizeThemeKey(themeKey)) {
      case "indigo": return "from-indigo-600 to-indigo-800 hover:from-indigo-500 hover:to-indigo-700 shadow-indigo-500/40";
      case "teal": return "from-teal-700 to-teal-900 hover:from-teal-600 hover:to-teal-800 shadow-teal-600/40";
      case "emerald": return "from-emerald-700 to-emerald-900 hover:from-emerald-600 hover:to-emerald-800 shadow-emerald-600/40";
      case "amber": return "from-amber-700 to-amber-900 hover:from-amber-600 hover:to-amber-800 shadow-amber-600/40";
      case "rose": return "from-rose-700 to-rose-900 hover:from-rose-600 hover:to-rose-800 shadow-rose-600/40";
      case "slate": return "from-slate-600 to-slate-800 hover:from-slate-500 hover:to-slate-700 shadow-slate-500/40";
      default: return PRIMARY_DEFAULT_GRADIENT_CLASS;
    }
  };

  const gradientClass = deptThemeKey
    ? getGradientClass(deptThemeKey)
    : PRIMARY_DEFAULT_GRADIENT_CLASS;

  return (
    <AppButton
      type="submit"
      variant="unstyled"
      onClick={onClick}
      disabled={isLoading}
      className={`
            w-full bg-gradient-to-br ${gradientClass} text-white
            font-bold py-4 rounded-2xl transition-all duration-300
            shadow-lg
            hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]
            flex items-center justify-center gap-2 text-base tracking-tight ${className}
            ${isLoading ? "opacity-90 cursor-wait" : ""}
            group overflow-hidden relative
        `}
    >
      {/* Shine Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-150%] skew-x-[-20deg] group-hover:animate-shine" />

      {isLoading ? (
        <>
          <Loader2 className="animate-spin" size={20} />
          <span>처리중...</span>
        </>
      ) : (
        <>
          {text}
          <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
        </>
      )}
    </AppButton>
  );
};
